/**
 * AppointmentsConfig — Rediseño UX/UI Sprint 07
 * Nav lateral con íconos · Banner perfil siempre visible · Cards de servicios inline
 * Mensajería Masiva: accesos directos a Campañas y Mensajes Programados
 * Sprint 07-B: CRUD completo de Secuencias + tab Agente IA separada
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link as RouterLink, useHistory } from "react-router-dom";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import Paper from "@material-ui/core/Paper";
import Box from "@material-ui/core/Box";
import Avatar from "@material-ui/core/Avatar";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Switch from "@material-ui/core/Switch";
import TextField from "@material-ui/core/TextField";
import IconButton from "@material-ui/core/IconButton";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";
import PersonIcon from "@material-ui/icons/Person";
import ScheduleIcon from "@material-ui/icons/Schedule";
import CategoryIcon from "@material-ui/icons/Category";
import NotificationsIcon from "@material-ui/icons/Notifications";
import AssistantIcon from "@material-ui/icons/Assistant";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import SendIcon from "@material-ui/icons/Send";
import AddIcon from "@material-ui/icons/Add";
import Chip from "@material-ui/core/Chip";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import MonetizationOnIcon from "@material-ui/icons/MonetizationOn";
import { toast } from "react-toastify";
import { useTheme } from "@material-ui/core/styles";
import { useStyles } from "./styles";
import useAppointmentsConfig from "./useAppointmentsConfig";
import SeqDialog from "./SeqDialog";
import SvcDialog from "./SvcDialog";
import ProfDialog from "./ProfDialog";
import ProfHoursDialog from "./ProfHoursDialog";
import BusinessDataDialog from "./BusinessDataDialog";
import {
  CONFIG_CARD_DEFS,
  SVC_FALLBACK_COLORS,
  seqCategoryStyle,
  svcChipStyle,
  SEQ_CATEGORIES,
  TRIGGER_UNITS,
  TRIGGER_DIRS,
  SEQ_DIALOG_EMPTY,
  SEQ_VARS_HINT,
  SVC_DIALOG_EMPTY,
  PROF_DIALOG_EMPTY,
  AGENT_SEQ_KEYS,
  DAY_NAMES,
  SLOT_OPTIONS,
  DEFAULT_DAYS,
  AVATAR_COLORS,
  avatarColor,
  roleChipProps,
} from "./appointments.config.constants";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

// ─── Nav items ─────────────────────────────────────────────────────────────
export default function AppointmentsConfig() {
  const classes = useStyles();
  const theme = useTheme();
  const agendaMode = (theme.agenda && theme.agenda.mode) || theme.palette.type;
  // Estado local y no en `useAppointmentsConfig`: el editor de horario se abre y
  // se cierra sin tocar nada del resto de la configuración, y el hook ya carga
  // con la gestión de servicios, equipo, horarios y secuencias.
  const [profHoursTarget, setProfHoursTarget] = useState(null);
  const {
    configCards,
    currentCard,
    equipoLoading,
    fetchEquipoData,
    fetchHorariosData,
    fetchSequences,
    fetchServicesData,
    globalSlot,
    businessData,
    businessDialogOpen,
    businessDialogSaving,
    handleOpenBusinessDialog,
    handleCloseBusinessDialog,
    handleSaveBusinessData,
    handleConfirmDeleteProf,
    handleConfirmDeleteSeq,
    handleConfirmDeleteSvc,
    handleDayToggle,
    handleLinkUser,
    handleOpenCreateProf,
    handleOpenCreateSeq,
    handleOpenCreateSvc,
    handleOpenEditProf,
    handleOpenEditSeq,
    handleOpenEditSvc,
    handleRecrearKanban,
    handleSaveHorarios,
    handleSaveProfDialog,
    handleSaveSeqDialog,
    handleSaveServiceDialog,
    handleTimeChange,
    handleToggleSequence,
    history,
    horariosLoading,
    horariosLocal,
    horariosModified,
    horariosSaving,
    loading,
    profDeleteTarget,
    profDialogData,
    profDialogMode,
    profDialogOpen,
    profDialogSaving,
    professionals,
    profile,
    profileId,
    recrearKanbanLoading,
    seqDeleteTarget,
    seqDialogData,
    seqDialogMode,
    seqDialogOpen,
    seqDialogSaving,
    seqLoading,
    sequences,
    services,
    servicesLoading,
    setAgenteIaLoading,
    setAppointmentPromptId,
    setEquipoLoading,
    setGlobalSlot,
    setHorariosLoading,
    setHorariosLocal,
    setHorariosModified,
    setHorariosSaving,
    setLoading,
    setProfDeleteTarget,
    setProfDialogData,
    setProfDialogMode,
    setProfDialogOpen,
    setProfDialogSaving,
    setProfessionals,
    setProfile,
    setProfileId,
    setPrompts,
    setRecrearKanbanLoading,
    setSeqDeleteTarget,
    setSeqDialogData,
    setSeqDialogMode,
    setSeqDialogOpen,
    setSeqDialogSaving,
    setSeqLoading,
    setSequences,
    setServices,
    setServicesLoading,
    setSubmenuIndex,
    setSvcDeleteTarget,
    setSvcDialogData,
    setSvcDialogMode,
    setSvcDialogOpen,
    setSvcDialogSaving,
    setUsers,
    submenuIndex,
    svcDeleteTarget,
    svcDialogData,
    svcDialogMode,
    svcDialogOpen,
    svcDialogSaving,
    users,
  } = useAppointmentsConfig();

  return (
    <div className={classes.configLayout}>

      {/* ── NAV LATERAL ─────────────────────────────────────────────── */}
      <aside className={classes.configNav}>
        <List dense disablePadding>
          {configCards.map((card, idx) => {
            const Icon = card.icon;
            return (
              <ListItem
                key={card.id}
                button
                selected={submenuIndex === idx}
                onClick={() => setSubmenuIndex(idx)}
                className={classes.configNavItem}
              >
                <ListItemIcon className={classes.configNavIcon}><Icon /></ListItemIcon>
                <ListItemText
                  primary={card.title}
                  primaryTypographyProps={{ variant: "body2" }}
                />
              </ListItem>
            );
          })}
        </List>
      </aside>

      {/* ── CONTENIDO ───────────────────────────────────────────────── */}
      <main className={classes.configContent}>

        {/* Banner primera vez */}
        {!loading && !profile && (
          <Paper className={classes.configFirstTimeBanner} elevation={0}>
            <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 4 }}>
              {i18n.t("appointmentsModule.config.firstTimeTitle")}
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
              {i18n.t("appointmentsModule.config.firstTimeSubtitle")}
            </Typography>
            <Button variant="contained" color="primary" size="small" component={RouterLink} to="/onboarding">
              {i18n.t("appointmentsModule.config.firstTimeCta")}
            </Button>
          </Paper>
        )}

        {/* Banner perfil activo */}
        {profile && (
          <Paper className={classes.configProfileCard} elevation={0}>
            <div>
              <Typography variant="caption" className={classes.configEyebrow}>
                {i18n.t("appointmentsModule.config.activeProfile")}
              </Typography>
              <Typography variant="subtitle1" style={{ fontWeight: 700, lineHeight: 1.3 }}>{profile}</Typography>
              <Typography variant="caption" color="textSecondary">
                {i18n.t("appointmentsModule.config.activeProfileSubtitle")}
              </Typography>
            </div>
            <div className={classes.configProfileCardActions}>
              {profileId != null && (
                <Button variant="contained" color="primary" size="small"
                  disabled={recrearKanbanLoading} onClick={handleRecrearKanban}
                >
                  {recrearKanbanLoading
                    ? i18n.t("appointmentsModule.config.kanbanProcessing")
                    : i18n.t("appointmentsModule.config.kanbanRecreate")}
                </Button>
              )}
              <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={handleOpenBusinessDialog}>
                {i18n.t("appointmentsModule.config.edit")}
              </Button>
            </div>
          </Paper>
        )}

        {/* ── SECCIONES ───────────────────────────────────────────── */}
        {currentCard && (

          /* ─── SERVICIOS ─────────────────────────────────────────── */
          currentCard.id === "servicios" ? (
            <div>
              <div className={classes.configSectionHeader}>
                <Typography variant="subtitle1" style={{ fontWeight: 700 }}>{currentCard.title}</Typography>
                <Button variant="contained" color="primary" size="small" startIcon={<AddIcon />} onClick={handleOpenCreateSvc}>
                  {i18n.t("appointmentsModule.config.addService")}
                </Button>
              </div>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                {currentCard.desc}
              </Typography>
              {servicesLoading ? (
                <Typography variant="body2" color="textSecondary">
                  {i18n.t("appointmentsModule.config.servicesLoading")}
                </Typography>
              ) : services.length === 0 ? (
                <Typography variant="body2" color="textSecondary" style={{ fontStyle: "italic" }}>
                  {i18n.t("appointmentsModule.config.noServices")}
                </Typography>
              ) : (
                services.map((svc, i) => {
                  const accentColor = SVC_FALLBACK_COLORS[i % SVC_FALLBACK_COLORS.length];
                  const hasPrice = svc.price != null && Number(svc.price) > 0;
                  return (
                    <Paper key={svc.id} className={classes.configSvcCard} elevation={0}>
                      <span className={classes.configSvcAccent} style={{ backgroundColor: accentColor }} />
                      <div className={classes.configSvcBody}>
                        <Box style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                          <Chip label={svc.serviceName} size="small"
                            style={{ height: 22, fontSize: "0.75rem", fontWeight: 700, ...svcChipStyle("name", agendaMode), borderWidth: 1, borderStyle: "solid" }}
                          />
                          {svc.durationMin && (
                            <Chip icon={<AccessTimeIcon style={{ fontSize: 13 }} />} label={`${svc.durationMin} min`} size="small"
                              style={{ height: 20, fontSize: "0.7rem", ...svcChipStyle("duration", agendaMode), borderWidth: 1, borderStyle: "solid" }}
                            />
                          )}
                          {hasPrice && (
                            <Chip icon={<MonetizationOnIcon style={{ fontSize: 13 }} />} label={`S/ ${Number(svc.price).toFixed(2)}`} size="small"
                              style={{ height: 20, fontSize: "0.7rem", ...svcChipStyle("price", agendaMode), borderWidth: 1, borderStyle: "solid" }}
                            />
                          )}
                        </Box>
                      </div>
                      <div className={classes.configSvcActions}>
                        <IconButton onClick={() => handleOpenEditSvc(svc)} title="Editar">
                          <EditIcon className={classes.configActionIcon} />
                        </IconButton>
                        <IconButton onClick={() => setSvcDeleteTarget(svc)} title="Eliminar">
                          <DeleteIcon className={classes.configDeleteIcon} />
                        </IconButton>
                      </div>
                    </Paper>
                  );
                })
              )}
            </div>

          /* ─── EQUIPO ────────────────────────────────────────────── */
          ) : currentCard.id === "equipo" ? (
            <div>
              <div className={classes.configSectionHeader}>
                <Typography variant="subtitle1" style={{ fontWeight: 700 }}>{currentCard.title}</Typography>
                <Button variant="contained" color="primary" size="small" startIcon={<AddIcon />} onClick={handleOpenCreateProf}>
                  {i18n.t("appointmentsModule.config.equipoAddBtn")}
                </Button>
              </div>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                {i18n.t("appointmentsModule.config.equipoIntro")}
              </Typography>
              {equipoLoading ? (
                <Typography variant="body2" color="textSecondary">
                  {i18n.t("appointmentsModule.config.loadingProfessionals")}
                </Typography>
              ) : professionals.length === 0 ? (
                <Typography variant="body2" color="textSecondary" style={{ fontStyle: "italic" }}>
                  {i18n.t("appointmentsModule.config.noProfessionals")}
                </Typography>
              ) : (
                professionals.map((p, idx) => {
                  const linkedUser = users.find((u) => u.id === p.whatsappUserId);
                  const chip = roleChipProps(linkedUser?.profile, agendaMode);
                  const initial = (p.name || "?")[0].toUpperCase();
                  const subtitle = [p.roleLabel, p.specialty].filter(Boolean).join(" · ");
                  return (
                    <Paper key={p.id} className={classes.configTeamRow} elevation={0}>
                      <Avatar style={{ width: 40, height: 40, flexShrink: 0, backgroundColor: avatarColor(idx), fontSize: "1rem", fontWeight: 700 }}>
                        {initial}
                      </Avatar>
                      <div className={classes.configTeamInfo}>
                        <div className={classes.configTeamNameRow}>
                          <Typography variant="subtitle2" style={{ fontWeight: 700 }}>{p.name}</Typography>
                          {linkedUser && (
                            <Chip label={chip.label} size="small"
                              style={{ height: 20, fontSize: "0.7rem", fontWeight: 600, backgroundColor: chip.bg, color: chip.color, border: `1px solid ${chip.border}` }}
                            />
                          )}
                        </div>
                        {subtitle && <Typography variant="caption" color="textSecondary">{subtitle}</Typography>}
                      </div>
                      <FormControl size="small" style={{ minWidth: 200, flexShrink: 0 }}>
                        <Select value={p.whatsappUserId ?? ""} onChange={(e) => handleLinkUser(p.id, e.target.value || null)}
                          displayEmpty disableUnderline style={{ fontSize: "0.8rem" }}
                        >
                          <MenuItem value="">{i18n.t("appointmentsModule.config.noneUser")}</MenuItem>
                          {users.map((u) => (
                            <MenuItem key={u.id} value={u.id}>{u.name} ({u.email})</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Box style={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
                        <IconButton onClick={() => setProfHoursTarget(p)} title="Horario de atención">
                          <AccessTimeIcon className={classes.configActionIcon} />
                        </IconButton>
                        <IconButton onClick={() => handleOpenEditProf(p)} title="Editar">
                          <EditIcon className={classes.configActionIcon} />
                        </IconButton>
                        <IconButton onClick={() => setProfDeleteTarget(p)} title="Eliminar">
                          <DeleteIcon className={classes.configDeleteIcon} />
                        </IconButton>
                      </Box>
                    </Paper>
                  );
                })
              )}
            </div>

          /* ─── HORARIOS ──────────────────────────────────────────── */
          ) : currentCard.id === "horarios" ? (
            <div>
              <div className={classes.configSectionHeader}>
                <Typography variant="subtitle1" style={{ fontWeight: 700 }}>{currentCard.title}</Typography>
                <Button variant="contained" color="primary" size="small"
                  disabled={!horariosModified || horariosSaving} onClick={handleSaveHorarios}
                >
                  {horariosSaving
                    ? i18n.t("appointmentsModule.config.horariosSaving")
                    : i18n.t("appointmentsModule.config.horariosSave")}
                </Button>
              </div>
              <div className={classes.configHorariosSlotRow}>
                <Typography variant="body2" style={{ fontWeight: 600, flexShrink: 0 }}>
                  {i18n.t("appointmentsModule.config.horariosSlotLabel")}
                </Typography>
                <FormControl size="small" style={{ minWidth: 110 }}>
                  <Select value={globalSlot} onChange={(e) => { setGlobalSlot(Number(e.target.value)); setHorariosModified(true); }} displayEmpty>
                    {SLOT_OPTIONS.map(m => <MenuItem key={m} value={m}>{m} min</MenuItem>)}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="textSecondary">{currentCard.desc}</Typography>
              </div>
              {horariosLoading ? (
                <Typography variant="body2" color="textSecondary">{i18n.t("appointmentsModule.config.loading")}</Typography>
              ) : (
                horariosLocal.map((day, idx) => (
                  <Paper key={day.dayOfWeek} className={classes.configHorariosRow} elevation={0}>
                    <Switch checked={!!day.isOpen} onChange={(e) => handleDayToggle(idx, e.target.checked)} color="primary" size="small" />
                    <Typography variant="subtitle2"
                      className={`${classes.configHorariosDay} ${day.isOpen ? "" : classes.configDayClosed}`}
                      style={{ fontWeight: 700 }}
                    >
                      {DAY_NAMES[day.dayOfWeek] || `Día ${day.dayOfWeek}`}
                    </Typography>
                    {day.isOpen ? (
                      <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <TextField type="time" size="small" value={day.openTime}
                          onChange={(e) => handleTimeChange(idx, "openTime", e.target.value)}
                          className={`${classes.configHorariosTime} ${classes.nativeDateInput}`}
                          inputProps={{ style: { fontSize: "0.85rem", padding: "6px 8px" } }} variant="outlined"
                        />
                        <Typography variant="caption" color="textSecondary">—</Typography>
                        <TextField type="time" size="small" value={day.closeTime}
                          onChange={(e) => handleTimeChange(idx, "closeTime", e.target.value)}
                          className={`${classes.configHorariosTime} ${classes.nativeDateInput}`}
                          inputProps={{ style: { fontSize: "0.85rem", padding: "6px 8px" } }} variant="outlined"
                        />
                      </Box>
                    ) : (
                      <Typography variant="caption" className={classes.configClosedLabel}>
                        {i18n.t("appointmentsModule.config.horariosClosed")}
                      </Typography>
                    )}
                  </Paper>
                ))
              )}
            </div>

          /* ─── SECUENCIAS NOTIFICACIONES ─────────────────────────── */
          ) : currentCard.id === "secuencias" ? (
            <div>
              <div className={classes.configSectionHeader}>
                <div>
                  <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                    {i18n.t("appointmentsModule.config.secuenciasTitle")}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {i18n.t("appointmentsModule.config.secuenciasSubtitle")}
                  </Typography>
                </div>
                <Button variant="contained" color="primary" size="small" startIcon={<AddIcon />} onClick={handleOpenCreateSeq}>
                  {i18n.t("appointmentsModule.config.secuenciasAddBtn")}
                </Button>
              </div>

              {seqLoading ? (
                <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
                  {i18n.t("appointmentsModule.config.secuenciasLoading")}
                </Typography>
              ) : sequences.filter(s => !AGENT_SEQ_KEYS.has(s.sequenceKey)).length === 0 ? (
                <Typography variant="body2" color="textSecondary" style={{ marginTop: 16, fontStyle: "italic" }}>
                  {i18n.t("appointmentsModule.config.secuenciasEmpty")}
                </Typography>
              ) : (
                <Box style={{ marginTop: 16 }}>
                  {sequences.filter(s => !AGENT_SEQ_KEYS.has(s.sequenceKey)).map((seq) => {
                    const catStyle = seqCategoryStyle(seq.category, agendaMode);
                    return (
                      <Paper key={seq.id} className={classes.configSeqCard} elevation={0}
                        style={{ opacity: seq.isEnabled ? 1 : 0.6 }}
                      >
                        {/* Dot */}
                        <span className={classes.configSeqDot} style={{ backgroundColor: catStyle.dot }} />

                        {/* Info */}
                        <div className={classes.configSeqInfo}>
                          <div className={classes.configSeqNameRow}>
                            <Typography variant="subtitle2" style={{ fontWeight: 700 }}>{seq.name}</Typography>
                            <Chip label={seq.category} size="small"
                              style={{ height: 18, fontSize: "0.65rem", fontWeight: 600,
                                backgroundColor: catStyle.bg, color: catStyle.color,
                                border: `1px solid ${catStyle.border}` }}
                            />
                          </div>
                          <Typography variant="caption" color="textSecondary">{seq.description}</Typography>
                          {seq.messageTemplate && (
                            <Typography variant="caption" className={classes.configSeqTemplate}>
                              {seq.messageTemplate.length > 85
                                ? `${seq.messageTemplate.slice(0, 85)}…`
                                : seq.messageTemplate}
                            </Typography>
                          )}
                        </div>

                        {/* Acciones */}
                        <Box style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
                          <IconButton onClick={() => handleOpenEditSeq(seq)} title="Editar">
                            <EditIcon className={classes.configActionIcon} />
                          </IconButton>
                          <IconButton onClick={() => setSeqDeleteTarget(seq)} title="Eliminar">
                            <DeleteIcon className={classes.configDeleteIcon} />
                          </IconButton>
                          <Switch checked={!!seq.isEnabled} onChange={() => handleToggleSequence(seq)} color="primary" size="small" />
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
              )}
            </div>

          /* ─── SECUENCIA AGENTE IA ───────────────────────────────── */
          ) : currentCard.id === "agente-ia" ? (
            <div>
              {/* Bloque 1: Secuencias de evento (confirmation, cancellation, reschedule) */}
              <div className={classes.configSectionHeader}>
                <div>
                  <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                    {currentCard.title}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {i18n.t("appointmentsModule.config.agentSeqSubtitle")}
                  </Typography>
                </div>
              </div>

              {seqLoading ? (
                <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
                  {i18n.t("appointmentsModule.config.secuenciasLoading")}
                </Typography>
              ) : (
                <Box style={{ marginTop: 8, marginBottom: 24 }}>
                  {sequences.filter(s => AGENT_SEQ_KEYS.has(s.sequenceKey)).map((seq) => {
                    const catStyle = seqCategoryStyle(seq.category, agendaMode, "Necesario");
                    return (
                      <Paper key={seq.id} className={classes.configSeqCard} elevation={0}
                        style={{ opacity: seq.isEnabled ? 1 : 0.6 }}
                      >
                        <span className={classes.configSeqDot} style={{ backgroundColor: catStyle.dot }} />
                        <div className={classes.configSeqInfo}>
                          <div className={classes.configSeqNameRow}>
                            <Typography variant="subtitle2" style={{ fontWeight: 700 }}>{seq.name}</Typography>
                            <Chip label={seq.category} size="small"
                              style={{ height: 18, fontSize: "0.65rem", fontWeight: 600,
                                backgroundColor: catStyle.bg, color: catStyle.color,
                                border: `1px solid ${catStyle.border}` }}
                            />
                          </div>
                          <Typography variant="caption" color="textSecondary">{seq.description}</Typography>
                          {seq.messageTemplate && (
                            <Typography variant="caption" className={classes.configSeqTemplate}>
                              {seq.messageTemplate.length > 85
                                ? `${seq.messageTemplate.slice(0, 85)}…`
                                : seq.messageTemplate}
                            </Typography>
                          )}
                        </div>
                        <Box style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
                          <IconButton onClick={() => handleOpenEditSeq(seq)} title="Editar">
                            <EditIcon className={classes.configActionIcon} />
                          </IconButton>
                          <IconButton onClick={() => setSeqDeleteTarget(seq)} title="Eliminar">
                            <DeleteIcon className={classes.configDeleteIcon} />
                          </IconButton>
                          <Switch checked={!!seq.isEnabled} onChange={() => handleToggleSequence(seq)} color="primary" size="small" />
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
              )}

            </div>

          /* ─── MENSAJERÍA MASIVA ──────────────────────────────────── */
          ) : currentCard.id === "mensajeria" ? (
            <div>
              <div className={classes.configSectionHeader}>
                <Typography variant="subtitle1" style={{ fontWeight: 700 }}>{currentCard.title}</Typography>
              </div>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 20 }}>
                {i18n.t("appointmentsModule.config.mensajeriaIntro")}
              </Typography>
              <Box style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <Paper className={classes.configSvcCard} elevation={0}>
                  <span className={classes.configSvcAccent} style={{ backgroundColor: "#8B5CF6" }} />
                  <div className={classes.configSvcBody}>
                    <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                      {i18n.t("appointmentsModule.config.mensajeriaCampaigns")}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {i18n.t("appointmentsModule.config.mensajeriaCampaignsDesc")}
                    </Typography>
                  </div>
                  <div className={classes.configSvcActions}>
                    <Button variant="outlined" size="small" component={RouterLink} to="/campaigns">
                      {i18n.t("appointmentsModule.config.mensajeriaCampaignsLink")}
                    </Button>
                  </div>
                </Paper>
                <Paper className={classes.configSvcCard} elevation={0}>
                  <span className={classes.configSvcAccent} style={{ backgroundColor: "#10B981" }} />
                  <div className={classes.configSvcBody}>
                    <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                      {i18n.t("appointmentsModule.config.mensajeriaScheduled")}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {i18n.t("appointmentsModule.config.mensajeriaScheduledDesc")}
                    </Typography>
                  </div>
                  <div className={classes.configSvcActions}>
                    <Button variant="outlined" size="small" component={RouterLink} to="/schedules">
                      {i18n.t("appointmentsModule.config.mensajeriaScheduledLink")}
                    </Button>
                  </div>
                </Paper>
              </Box>
            </div>

          ) : null
        )}
      </main>

      {/* ── DIALOGO DATOS DEL NEGOCIO (edición no destructiva) ───────── */}
      <BusinessDataDialog
        open={businessDialogOpen}
        data={businessData}
        saving={businessDialogSaving}
        onClose={handleCloseBusinessDialog}
        onSave={handleSaveBusinessData}
      />

      {/* ── DIALOGO CREAR / EDITAR SECUENCIA ────────────────────────── */}
      <SeqDialog
        seqDialogOpen={seqDialogOpen}
        setSeqDialogOpen={setSeqDialogOpen}
        seqDialogMode={seqDialogMode}
        seqDialogData={seqDialogData}
        setSeqDialogData={setSeqDialogData}
        handleSaveSeqDialog={handleSaveSeqDialog}
        seqDialogSaving={seqDialogSaving}
      />

      {/* ── DIALOGO CONFIRMAR ELIMINAR ───────────────────────────────── */}
      <Dialog open={!!seqDeleteTarget} onClose={() => setSeqDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{i18n.t("appointmentsModule.config.seqDialogDeleteTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>{seqDeleteTarget?.name}</strong>
            {" — "}
            {i18n.t("appointmentsModule.config.seqDialogDeleteDesc")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSeqDeleteTarget(null)}>Cancelar</Button>
          <Button onClick={handleConfirmDeleteSeq} color="secondary" variant="contained">
            {i18n.t("appointmentsModule.config.seqDialogDeleteOk")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── DIALOGO CREAR / EDITAR SERVICIO ─────────────────────────── */}
      <SvcDialog
        svcDialogOpen={svcDialogOpen}
        setSvcDialogOpen={setSvcDialogOpen}
        svcDialogMode={svcDialogMode}
        svcDialogData={svcDialogData}
        setSvcDialogData={setSvcDialogData}
        svcDialogSaving={svcDialogSaving}
        handleSaveServiceDialog={handleSaveServiceDialog}
      />

      {/* ── DIALOGO CONFIRMAR ELIMINAR SERVICIO ─────────────────────── */}
      <Dialog open={!!svcDeleteTarget} onClose={() => setSvcDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>¿Eliminar servicio?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>{svcDeleteTarget?.serviceName}</strong>
            {" — Esta acción no se puede deshacer."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSvcDeleteTarget(null)}>Cancelar</Button>
          <Button onClick={handleConfirmDeleteSvc} color="secondary" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── DIALOGO CREAR / EDITAR PROFESIONAL ──────────────────────── */}
      <ProfDialog
        profDialogOpen={profDialogOpen}
        setProfDialogOpen={setProfDialogOpen}
        profDialogMode={profDialogMode}
        profDialogData={profDialogData}
        setProfDialogData={setProfDialogData}
        profDialogSaving={profDialogSaving}
        handleSaveProfDialog={handleSaveProfDialog}
        users={users}
      />

      {/* ── DIALOGO HORARIO DEL PROFESIONAL (doc 0396) ───────────────── */}
      <ProfHoursDialog
        open={!!profHoursTarget}
        professional={profHoursTarget}
        onClose={() => setProfHoursTarget(null)}
      />

      {/* ── DIALOGO CONFIRMAR ELIMINAR PROFESIONAL ───────────────────── */}
      <Dialog open={!!profDeleteTarget} onClose={() => setProfDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>¿Eliminar miembro del equipo?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>{profDeleteTarget?.name}</strong>
            {" — Esta acción no se puede deshacer."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProfDeleteTarget(null)}>Cancelar</Button>
          <Button onClick={handleConfirmDeleteProf} color="secondary" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

    </div>
  );
}
